import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import EPublisherLayout, { KB_COLORS } from '../../layouts/EPublisherLayout';
import FaIcon from './epublisher-kb/icons';
import {
  FAQ_PAGE_ID,
  HOME_PAGE_ID,
  KB_HOME_PATH,
  MENU_ITEMS,
  SITE_TITLE,
  SUPPORT_PHONE,
  articleBySlug,
  catBySlug,
  categoryChain,
  childrenOf,
  directArticles,
  escapeRegExp,
  faqBySlug,
  faqList,
  flatCategories,
  formatDate,
  kbUrl,
  makeSnippet,
  neighbours,
  pageById,
  pageChain,
  prepareHtml,
  primaryCategory,
  rootCategories,
  search,
  tokenize
} from './epublisher-kb/model';

const { accent, accentDark, heading, muted, line, text: bodyText } = KB_COLORS;

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/* -------------------------------------------------------------------------- */
/* Shared pieces                                                               */
/* -------------------------------------------------------------------------- */

const linkSx = {
  color: accent,
  textDecoration: 'none',
  '&:hover, &:focus-visible': { color: accentDark, textDecoration: 'underline' }
};

const KBLink = ({ to, sx, children, ...rest }) => (
  <Box component={RouterLink} to={to} sx={{ ...linkSx, ...sx }} {...rest}>
    {children}
  </Box>
);

/** Centered, uppercase breadcrumbs, e.g. HOME › KNOWLEDGE BASE › CONTENT CREATORS */
const Crumbs = ({ items }) =>
  items.length ? (
    <Box
      component="nav"
      aria-label="breadcrumb"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        mb: '30px',
        color: muted,
        fontSize: 14,
        textTransform: 'uppercase',
        lineHeight: 1.4
      }}
    >
      {items.map((item, i) => (
        <React.Fragment key={`${item.label}-${i}`}>
          {i > 0 && <NavigateNextIcon sx={{ fontSize: 16, mx: '2px' }} />}
          {item.to ? (
            <Box component={RouterLink} to={item.to} sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: accent } }}>
              {item.label}
            </Box>
          ) : (
            <span>{item.label}</span>
          )}
        </React.Fragment>
      ))}
    </Box>
  ) : null;

const Title = ({ children }) => (
  <Typography
    component="h1"
    sx={{
      color: heading,
      fontSize: { xs: 30, md: 42 },
      fontWeight: 700,
      lineHeight: 1.5,
      textAlign: 'center',
      m: '0 0 30px',
      overflowWrap: 'anywhere'
    }}
  >
    {children}
  </Typography>
);

const bodySx = {
  color: bodyText,
  fontSize: 16,
  lineHeight: '27px',
  overflowWrap: 'anywhere',
  '& p': { m: '0 0 30px' },
  '& > :last-child': { mb: 0 },
  '& h2, & h3, & h4, & h5, & h6': { color: heading, fontWeight: 700, lineHeight: 1.3, m: '40px 0 20px' },
  '& h2': { fontSize: 30 },
  '& h3': { fontSize: 24 },
  '& h4': { fontSize: 20 },
  '& h5': { fontSize: 18 },
  '& h6': { fontSize: 16 },
  '& ul, & ol': { pl: '28px', m: '0 0 30px' },
  '& li': { mb: '6px' },
  '& a': { color: accent, textDecoration: 'none', '&:hover': { color: accentDark, textDecoration: 'underline' } },
  '& img': { maxWidth: '100%', height: 'auto !important', verticalAlign: 'middle' },
  '& video': { display: 'block', maxWidth: '100%', height: 'auto', m: '0 0 30px', bgcolor: '#000' },
  '& table': { display: 'block', maxWidth: '100%', overflowX: 'auto', borderCollapse: 'collapse', mb: '30px' },
  '& td, & th': { border: `1px solid ${line}`, p: '6px 10px' },
  '& ul.kb-toc': {
    listStyle: 'none',
    p: '18px 28px',
    border: `1px solid ${line}`,
    borderRadius: '3px',
    '& li': { mb: '4px' }
  },
  '& a.kb-pdf-link': {
    position: 'relative',
    display: 'inline-flex',
    fontSize: 56,
    lineHeight: 1,
    color: accent,
    transition: 'transform .15s',
    '&:hover': { color: accentDark, transform: 'scale(1.1)' }
  },
  '& .kb-sr-only': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 1,
    height: 1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap'
  },
  '& .kb-video': { position: 'relative', width: '100%', aspectRatio: '16 / 9', m: '0 0 30px', bgcolor: '#000' },
  '& .kb-video iframe': { position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }
};

/** Sanitised static HTML; links to other KB pages navigate inside the app. */
const Html = ({ html, sx }) => {
  const navigate = useNavigate();
  const prepared = useMemo(() => prepareHtml(html), [html]);
  const rootRef = useRef(null);

  // Former animated GIFs are short silent videos: play them while visible, pause them when scrolled away
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const videos = [...root.querySelectorAll('video.kb-gif')];
    if (!videos.length) return undefined;
    videos.forEach((v) => {
      v.muted = true; // required for autoplay
    });
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          const v = entry.target;
          if (entry.isIntersecting) {
            const p = v.play();
            if (p && p.catch) p.catch(() => {}); // autoplay blocked: the controls remain available
          } else {
            v.pause();
          }
        }),
      { threshold: 0.25 }
    );
    videos.forEach((v) => observer.observe(v));
    return () => observer.disconnect();
  }, [prepared]);

  const onClick = (e) => {
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button > 0) return;
    const anchor = e.target && e.target.closest ? e.target.closest('a') : null;
    if (!anchor || anchor.target === '_blank') return;
    const href = anchor.getAttribute('href') || '';
    if (href === KB_HOME_PATH || href.startsWith(`${KB_HOME_PATH}?`)) {
      e.preventDefault();
      navigate(href);
    }
  };

  return <Box ref={rootRef} onClick={onClick} sx={{ ...bodySx, ...sx }} dangerouslySetInnerHTML={{ __html: prepared }} />;
};

/* -------------------------------------------------------------------------- */
/* Sidebar                                                                     */
/* -------------------------------------------------------------------------- */

const widgetSx = { p: { xs: '30px 24px', md: '40px 40px' }, borderBottom: `1px solid ${line}`, '&:last-of-type': { borderBottom: 0 } };
const widgetTitleSx = { color: muted, fontSize: 14, fontWeight: 700, lineHeight: 1.3, m: '0 0 30px' };

const Sidebar = () => (
  <>
    <Box component="nav" aria-label="Knowledge base menu" sx={{ ...widgetSx, pt: { md: '54px' } }}>
      <KBLink to={kbUrl.home()} sx={{ display: 'inline-block', lineHeight: '28px' }}>
        Home
      </KBLink>
      <Box component="ul" sx={{ listStyle: 'none', m: 0, pl: '20px' }}>
        {MENU_ITEMS.map((item) => (
          <li key={item.label}>
            <KBLink to={item.to} sx={{ display: 'inline-block', lineHeight: '28px' }}>
              {item.label}
            </KBLink>
          </li>
        ))}
      </Box>
    </Box>

    <Box sx={widgetSx}>
      <Typography component="h3" sx={widgetTitleSx}>
        Contact Us
      </Typography>
      <Typography sx={{ lineHeight: '27px' }}>
        If you are unable to find what your looking for in this knowledge base, feel free to contact us on{' '}
        <strong>{SUPPORT_PHONE}</strong> and dial <strong>1</strong> for support.
      </Typography>
    </Box>

    <Box sx={widgetSx}>
      <Typography component="h3" sx={widgetTitleSx}>
        FAQ
      </Typography>
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {faqList.map((faq) => (
          <Box component="li" key={faq.slug} sx={{ mb: '14px', lineHeight: 1.45 }}>
            <KBLink to={kbUrl.faq(faq.slug)}>{faq.title}</KBLink>
          </Box>
        ))}
      </Box>
    </Box>
  </>
);

/* -------------------------------------------------------------------------- */
/* Lists                                                                       */
/* -------------------------------------------------------------------------- */

/** Folder box for a (sub)category: "Name (3 Articles)" */
const CategoryBox = ({ category }) => {
  const count = directArticles[category.slug].length;
  return (
    <Box
      component={RouterLink}
      to={kbUrl.category(category.slug)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        minHeight: 96,
        p: '20px 24px',
        border: `1px solid ${line}`,
        borderRadius: '3px',
        textDecoration: 'none',
        transition: 'border-color .15s, box-shadow .15s',
        '&:hover, &:focus-visible': { borderColor: accent, boxShadow: '0 2px 10px rgba(69,191,150,0.18)' }
      }}
    >
      <FolderOpenOutlinedIcon sx={{ color: muted, flexShrink: 0 }} />
      <Box sx={{ minWidth: 0, fontSize: 14, lineHeight: 1.4 }}>
        <Box component="span" sx={{ color: accent, fontWeight: 700 }}>
          {category.name}
        </Box>{' '}
        <Box component="span" sx={{ color: muted, fontSize: 12 }}>
          ({plural(count, 'Article')})
        </Box>
      </Box>
    </Box>
  );
};

const CategoryGrid = ({ categories }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: '20px', mb: '40px' }}>
    {categories.map((c) => (
      <CategoryBox key={c.slug} category={c} />
    ))}
  </Box>
);

const ArticleLinks = ({ articles }) => (
  <Box>
    <Typography sx={{ color: muted, fontSize: 13, letterSpacing: '0.05em', textTransform: 'uppercase', mb: '20px' }}>
      {plural(articles.length, 'Article')}
    </Typography>
    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {articles.map((a) => (
        <Box component="li" key={a.slug} sx={{ display: 'flex', alignItems: 'flex-start', gap: '14px', mb: '20px' }}>
          <DescriptionOutlinedIcon sx={{ color: muted, fontSize: 22, mt: '1px', flexShrink: 0 }} />
          <KBLink to={kbUrl.article(a.slug)} sx={{ lineHeight: '24px' }}>
            {a.displayTitle}
          </KBLink>
        </Box>
      ))}
    </Box>
  </Box>
);

/* -------------------------------------------------------------------------- */
/* Elementor page renderer (cards, headings, text, FAQ list)                   */
/* -------------------------------------------------------------------------- */

const alignOf = (a) => (a === 'center' || a === 'right' || a === 'left' ? a : 'left');
const HEADING_SIZES = { h1: 42, h2: 32, h3: 26, h4: 18, h5: 16, h6: 14 };

const Card = ({ node, bordered }) => {
  const navigate = useNavigate();
  const inner = (
    <Box
      sx={{
        height: '100%',
        textAlign: 'center',
        p: bordered ? '28px 20px 24px' : '16px 10px',
        border: bordered ? '2px solid rgba(2,1,1,0.08)' : 0,
        borderRadius: bordered ? '25px' : 0,
        transition: 'border-color .15s, box-shadow .15s',
        '&:hover': bordered ? { borderColor: 'rgba(69,191,150,0.6)', boxShadow: '0 4px 14px rgba(0,0,0,0.06)' } : {}
      }}
    >
      {node.icon && <FaIcon name={node.icon} sx={{ fontSize: 40, color: '#69727D', display: 'block', mx: 'auto', mb: '14px' }} />}
      {node.title && (
        <Typography component="h4" sx={{ color: '#000', fontSize: 20, fontWeight: 400, lineHeight: '26px', m: 0 }}>
          {node.title}
        </Typography>
      )}
      {node.description && <Typography sx={{ color: bodyText, lineHeight: '27px', mt: '10px' }}>{node.description}</Typography>}
    </Box>
  );
  if (!node.href) return inner;
  return (
    <Box
      component="a"
      href={node.href}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
        e.preventDefault();
        navigate(node.href);
      }}
      sx={{ display: 'block', height: '100%', color: 'inherit', textDecoration: 'none', '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: 2, borderRadius: '25px' } }}
    >
      {inner}
    </Box>
  );
};

const FaqGrid = () => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, gap: '40px 40px' }}>
    {faqList.map((faq) => (
      <Box key={faq.slug} sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography component="h2" sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.45, m: '0 0 10px' }}>
          <KBLink to={kbUrl.faq(faq.slug)}>{faq.displayTitle}</KBLink>
        </Typography>
        <Typography sx={{ fontSize: 14, lineHeight: 1.65, mb: '14px' }}>{faq.excerpt}</Typography>
        <KBLink to={kbUrl.faq(faq.slug)} sx={{ mt: 'auto', fontSize: 12, fontWeight: 700 }} aria-label={`Read more about ${faq.title}`}>
          Read More »
        </KBLink>
      </Box>
    ))}
  </Box>
);

const Node = ({ node, bordered }) => {
  switch (node.type) {
    case 'section':
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: '-10px', mb: '10px' }}>
          {node.children.map((c, i) => (
            <Node key={i} node={c} bordered={bordered} />
          ))}
        </Box>
      );
    case 'column': {
      const size = Math.min(Math.max(Number(node.size) || 100, 10), 100);
      return (
        <Box
          sx={{
            boxSizing: 'border-box',
            p: '10px',
            minWidth: 0,
            flex: {
              xs: '0 0 100%',
              sm: `0 0 ${size >= 50 ? size : 50}%`,
              md: `0 0 ${size}%`
            },
            maxWidth: { xs: '100%', sm: `${size >= 50 ? size : 50}%`, md: `${size}%` },
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}
        >
          {node.children.map((c, i) => (
            <Node key={i} node={c} bordered={bordered} />
          ))}
        </Box>
      );
    }
    case 'heading':
      return (
        <Typography
          component={/^h[1-6]$/.test(node.level) ? node.level : 'h3'}
          sx={{
            color: heading,
            fontWeight: 700,
            lineHeight: 1.4,
            fontSize: HEADING_SIZES[node.level] || 18,
            textAlign: alignOf(node.align),
            m: 0
          }}
        >
          {node.text}
        </Typography>
      );
    case 'text':
      return <Html html={node.html} sx={{ textAlign: alignOf(node.align), '& h3': { fontSize: 24, fontWeight: 400, lineHeight: 1.3, m: '0 0 30px' } }} />;
    case 'card':
      return (
        <Box sx={{ mb: bordered ? '10px' : 0, mx: bordered ? '5px' : 0 }}>
          <Card node={node} bordered={bordered} />
        </Box>
      );
    case 'faqList':
      return <FaqGrid />;
    default:
      return null;
  }
};

/* -------------------------------------------------------------------------- */
/* Views                                                                       */
/* -------------------------------------------------------------------------- */

const NotFound = ({ message }) => (
  <Box sx={{ textAlign: 'center', py: 4 }}>
    <Title>{message}</Title>
    <KBLink to={kbUrl.home()}>Back to the Knowledge Base home</KBLink>
  </Box>
);

const PageView = ({ id }) => {
  const page = pageById[id];
  if (!page) return <NotFound message="Page not found" />;
  const isHome = Number(page.id) === HOME_PAGE_ID;
  const crumbs = isHome
    ? []
    : [{ label: 'Home', to: kbUrl.home() }, ...pageChain(page.id).slice(0, -1).map((p) => ({ label: p.title, to: kbUrl.page(p.id) }))];
  // The home page and the FAQ index have no breadcrumb trail on the original
  const showCrumbs = crumbs.length > 1;
  return (
    <>
      {showCrumbs && <Crumbs items={crumbs} />}
      {!isHome && <Title>{page.title}</Title>}
      <Node node={{ type: 'section', children: [{ type: 'column', size: 100, children: page.tree }] }} bordered={isHome} />
    </>
  );
};

const CategoryView = ({ slug }) => {
  const category = catBySlug[slug];
  if (!category) return <NotFound message="Category not found" />;
  const kids = childrenOf[slug] || [];
  const direct = directArticles[slug];
  const crumbs = [
    { label: 'Home', to: kbUrl.home() },
    { label: 'Knowledge Base', to: kbUrl.archive() },
    ...categoryChain(slug).slice(0, -1).map((c) => ({ label: c.name, to: kbUrl.category(c.slug) }))
  ];
  return (
    <>
      <Crumbs items={crumbs} />
      <Title>{category.name}</Title>
      {kids.length > 0 && <CategoryGrid categories={kids} />}
      {direct.length > 0 && <ArticleLinks articles={direct} />}
      {kids.length === 0 && direct.length === 0 && <Typography sx={{ textAlign: 'center' }}>There are no articles in this category yet.</Typography>}
    </>
  );
};

const ArchiveView = () => (
  <>
    <Crumbs items={[{ label: 'Home', to: kbUrl.home() }]} />
    <Title>Knowledge Base</Title>
    <CategoryGrid categories={rootCategories} />
  </>
);

const tagLinkSx = { ...linkSx };

const ArticleView = ({ slug }) => {
  const article = articleBySlug[slug];
  if (!article) return <NotFound message="Article not found" />;
  const primary = primaryCategory(article);
  const { previous, next } = neighbours(slug);
  const crumbs = [
    { label: 'Home', to: kbUrl.home() },
    { label: 'Knowledge Base', to: kbUrl.archive() },
    ...categoryChain(primary).map((c) => ({ label: c.name, to: kbUrl.category(c.slug) }))
  ];
  const terms = [
    ...[...article.categories]
      .sort((a, b) => catBySlug[a].name.localeCompare(catBySlug[b].name))
      .map((s) => ({ label: catBySlug[s].name, to: kbUrl.category(s) })),
    ...article.tags.map((t) => ({ label: t, to: kbUrl.search(t) }))
  ];
  return (
    <>
      <Crumbs items={crumbs} />
      <Title>{article.displayTitle}</Title>
      <Html html={article.html} />

      <Box sx={{ mt: '60px', fontSize: 13, color: muted, letterSpacing: '0.05em' }}>
        <Box sx={{ textTransform: 'uppercase', mb: '6px' }}>{formatDate(article.date)}</Box>
        {terms.length > 0 && (
          <Box sx={{ fontSize: 14, letterSpacing: 0, lineHeight: 1.7 }}>
            {terms.map((t, i) => (
              <React.Fragment key={`${t.label}-${i}`}>
                <KBLink to={t.to} sx={tagLinkSx}>
                  {t.label}
                </KBLink>
                {i < terms.length - 1 ? ', ' : ''}
              </React.Fragment>
            ))}
          </Box>
        )}
      </Box>

      {(previous || next) && (
        <Box
          sx={{
            mt: '40px',
            pt: '30px',
            borderTop: `1px solid ${line}`,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: '24px'
          }}
        >
          <Box>
            {previous && (
              <>
                <Typography component="h6" sx={{ color: muted, fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', mb: '6px' }}>
                  Previous
                </Typography>
                <KBLink to={kbUrl.article(previous.slug)}>{previous.displayTitle}</KBLink>
              </>
            )}
          </Box>
          <Box sx={{ textAlign: { sm: 'right' } }}>
            {next && (
              <>
                <Typography component="h6" sx={{ color: muted, fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', mb: '6px' }}>
                  Next
                </Typography>
                <KBLink to={kbUrl.article(next.slug)}>{next.displayTitle}</KBLink>
              </>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

const FaqView = ({ slug }) => {
  const faq = faqBySlug[slug];
  if (!faq) return <NotFound message="Question not found" />;
  return (
    <>
      <Crumbs
        items={[
          { label: 'Home', to: kbUrl.home() },
          { label: 'Frequently Asked Questions', to: kbUrl.page(FAQ_PAGE_ID) }
        ]}
      />
      <Title>{faq.displayTitle}</Title>
      <Html html={faq.html} />
    </>
  );
};

const Highlight = ({ value, terms }) => {
  if (!terms.length) return <>{value}</>;
  const re = new RegExp(`(${terms.map(escapeRegExp).join('|')})`, 'gi');
  return (
    <>
      {value.split(re).map((part, i) =>
        i % 2 === 1 ? (
          <mark key={i} style={{ backgroundColor: '#D8F3E8', color: 'inherit', padding: 0 }}>
            {part}
          </mark>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
};

const SearchView = ({ query }) => {
  const [categoryFilter, setCategoryFilter] = useState('');
  const terms = useMemo(() => tokenize(query), [query]);
  const results = useMemo(() => search(terms, categoryFilter), [terms, categoryFilter]);

  return (
    <>
      <Crumbs items={[{ label: 'Home', to: kbUrl.home() }, { label: 'Search' }]} />
      <Title>Search Results</Title>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: '30px'
        }}
      >
        <Typography aria-live="polite" sx={{ color: heading, fontWeight: 700 }}>
          {plural(results.length, 'result')} for “{query.trim()}”
        </Typography>
        <FormControl size="small" sx={{ minWidth: { sm: 240 } }}>
          <InputLabel id="kb-cat-filter-label">Filter by category</InputLabel>
          <Select
            labelId="kb-cat-filter-label"
            label="Filter by category"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <MenuItem value="">All categories</MenuItem>
            {flatCategories.map((c) => (
              <MenuItem key={c.slug} value={c.slug} sx={{ pl: 2 + c.depth * 2 }}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {results.length === 0 ? (
        <Typography sx={{ textAlign: 'center', py: 3 }}>
          No results match your search. Try different keywords{categoryFilter ? ' or choose “All categories”' : ''}.
        </Typography>
      ) : (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {results.map((item) => (
            <Box component="li" key={`${item.kind}-${item.slug}`} sx={{ mb: '30px' }}>
              <Typography component="h2" sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.4, m: '0 0 4px' }}>
                <KBLink to={item.kind === 'faq' ? kbUrl.faq(item.slug) : kbUrl.article(item.slug)}>
                  <Highlight value={item.displayTitle} terms={terms} />
                </KBLink>
                {item.kind === 'faq' && (
                  <Box component="span" sx={{ ml: 1, color: muted, fontSize: 11, fontWeight: 700, letterSpacing: '0.05em' }}>
                    FAQ
                  </Box>
                )}
              </Typography>
              <Typography sx={{ fontSize: 14, lineHeight: 1.65 }}>
                <Highlight value={makeSnippet(item, terms)} terms={terms} />
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </>
  );
};

/* -------------------------------------------------------------------------- */
/* Page                                                                        */
/* -------------------------------------------------------------------------- */

const EPublisherKB = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const query = params.get('q') || '';
  const articleSlug = params.get('article');
  const faqSlug = params.get('faq');
  const categorySlug = params.get('category');
  const pageParam = params.get('page');
  const isSearching = query.trim().length > 0;
  const isArchive = params.has('kb');

  let view;
  let content;
  let title = '';
  let withSidebar = true;

  if (isSearching) {
    view = 'search';
    title = `Search: ${query.trim()}`;
    content = <SearchView query={query} />;
  } else if (articleSlug) {
    view = `article:${articleSlug}`;
    title = (articleBySlug[articleSlug] || {}).displayTitle || 'Article not found';
    content = <ArticleView slug={articleSlug} />;
  } else if (faqSlug) {
    view = `faq:${faqSlug}`;
    title = (faqBySlug[faqSlug] || {}).displayTitle || 'Question not found';
    content = <FaqView slug={faqSlug} />;
  } else if (categorySlug) {
    view = `category:${categorySlug}`;
    title = (catBySlug[categorySlug] || {}).name || 'Category not found';
    content = <CategoryView slug={categorySlug} />;
  } else if (isArchive) {
    view = 'archive';
    title = 'Knowledge Base';
    content = <ArchiveView />;
  } else {
    const id = Number(pageParam) || HOME_PAGE_ID;
    view = `page:${id}`;
    title = id === HOME_PAGE_ID ? '' : (pageById[id] || {}).title || 'Page not found';
    withSidebar = false;
    content = <PageView id={id} />;
  }

  useEffect(() => {
    document.title = title ? `${title} – ${SITE_TITLE}` : SITE_TITLE;
  }, [title]);

  // Scroll to the top when moving between pages (not when only the in-page #hash changes)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [view, query]);

  return (
    <EPublisherLayout
      query={query}
      onSearch={(term) => {
        if (term) navigate(kbUrl.search(term));
        else navigate(kbUrl.home());
      }}
      sidebar={withSidebar ? <Sidebar /> : null}
    >
      {content}
    </EPublisherLayout>
  );
};

export default EPublisherKB;
